// navigation-helpers.js

import { useAbility } from '@/plugins/casl/composables/useAbility'

function userHasRole(roleName) {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage?.getItem('fullUserData') : null
    if (!raw) return false
    const user = JSON.parse(raw)
    const roles = user?.roles
    if (!Array.isArray(roles)) return false
    return roles.some(r => (typeof r === 'string' ? r : r?.name) === roleName)
  } catch (_) {
    return false
  }
}

/**
 * Check if user can view a navigation item based on generic or team-specific permissions
 * 
 * This function extends the basic CASL permission check to also consider team-specific
 * permissions. For example, if the navigation requires 'read' on 'Shipment', this will
 * also check if the user has any team-specific permissions like 'read-GD-parcels'.
 * 
 * @param {string} action - The required action (e.g., 'read', 'create')
 * @param {string} subject - The required subject (e.g., 'Shipment', 'Customers')
 * @returns {boolean} - Whether the user can view the navigation item
 */
export function canViewNavItem(action, subject, opts = {}) {
  const { ability } = useAbility()

  // Superadmin-gated entries: only show for users with the 'superadmin' role.
  // (Settings, User Management.)
  if (opts.requiresSuperadmin) {
    return userHasRole('superadmin')
  }

  // Admin-gated entries: visible to admin OR superadmin only.
  // (Container Arrivals — auto-created by the importer, but admins still
  // need the manual list/edit page for legacy data and arrival-date fixes.)
  if (opts.requiresAdmin) {
    return userHasRole('admin') || userHasRole('superadmin')
  }

  // Check for superadmin access first
  if (ability.can('manage', 'all')) {
    return true
  }
  
  // Map common navigation subjects to their backend equivalents
  const subjectMap = {
    'Shipment': 'Shipment',        // Generic shipment access
    'Parcels': 'Parcels',          // Team-specific: access-{team}-parcels -> Parcels
    'Packages': 'Packages',        // Team-specific: access-{team}-packages -> Packages  
    'PackingLists': 'PackingLists', // Team-specific: access-{team}-packing-lists -> PackingLists
    'ContainerArrivals': 'ContainerArrivals', // Container operations
    'Warehouse': 'Warehouse',      // Warehouse operations
    'Products': 'Products',        // Product management
    'Analytics': 'Analytics',      // Reports and analytics
    'Customers': 'Customers'       // Customer management
  }
  
  const mappedSubject = subjectMap[subject] || subject
  
  // Check if user has permission for this action and subject
  // This will work for both generic permissions (read_parcels) and 
  // team-specific permissions (read-GD-parcels) since the parser
  // converts 'read-GD-parcels' to { action: 'read', subject: 'Parcels' }
  return ability.can(action, mappedSubject)
}

/**
 * Check if user has team-specific permission for an action and subject
 * 
 * @param {object} ability - CASL ability instance
 * @param {string} action - The action to check (e.g., 'read', 'create')
 * @param {string} subject - The subject to check (e.g., 'Parcels', 'Customers')
 * @returns {boolean} - Whether the user has the permission
 */
function hasTeamSpecificPermission(ability, action, subject) {
  // Check if user has the action on the subject for any team
  // This works because our permission parser converts 'read-GD-parcels' to
  // { action: 'read', subject: 'Parcels', team: 'GD' }
  
  try {
    // CASL will check all rules, including team-specific ones
    return ability.can(action, subject)
  } catch (error) {
    return false
  }
}

/**
 * Get all possible team action patterns for a given generic action and subject
 * 
 * @param {string} action - Generic action like 'read', 'create'
 * @param {string} subject - Generic subject like 'Shipment', 'Customers'
 * @returns {string[]} - Array of team-specific permission patterns
 */
function getTeamActions(action, subject) {
  // Get team codes from user data or use common ones as fallback
  const teamCodes = getAvailableTeamCodes()
  const actions = []
  
  if (subject === 'Shipment') {
    // Map generic shipment actions to team-specific parcel actions
    const actionMap = {
      'read': ['read', 'access', 'view'],
      'create': ['create'],
      'edit': ['edit', 'update'],
      'delete': ['delete'],
      'manage': ['manage']
    }
    
    const specificActions = actionMap[action] || [action]
    
    teamCodes.forEach(team => {
      specificActions.forEach(specificAction => {
        // Generate patterns like: read-GD-parcels, access-GD-parcels, etc.
        actions.push(`${specificAction}-${team.toLowerCase()}-parcels`)
        actions.push(`${specificAction}-${team.toUpperCase()}-parcels`)
      })
    })
  }
  
  if (subject === 'Customers') {
    teamCodes.forEach(team => {
      // Generate patterns like: read-GD-customers, create-GD-customers, etc.
      actions.push(`${action}-${team.toLowerCase()}-customers`)
      actions.push(`${action}-${team.toUpperCase()}-customers`)
    })
  }
  
  return actions
}

/**
 * Get available team codes from user permissions or fallback to common ones
 * 
 * @returns {string[]} - Array of team codes
 */
function getAvailableTeamCodes() {
  try {
    const { ability } = useAbility()
    
    // Try to extract team codes from user's actual permissions
    const rules = ability.rules || []
    const teamCodes = new Set()
    
    rules.forEach(rule => {
      if (rule.action && typeof rule.action === 'string') {
        // Look for team patterns in permissions like 'read-GD-parcels'
        const match = rule.action.match(/-(GD|GB|JP|[A-Z]{2})(-|$)/)
        if (match) {
          teamCodes.add(match[1])
        }
      }
      
      if (rule.subject && typeof rule.subject === 'string') {
        // Look for team patterns in subjects like 'GD-parcels'
        const match = rule.subject.match(/^(GD|GB|JP|[A-Z]{2})(-|$)/)
        if (match) {
          teamCodes.add(match[1])
        }
      }
    })
    
    // If we found team codes from permissions, use those
    if (teamCodes.size > 0) {
      return Array.from(teamCodes)
    }
  } catch (error) {
    // Could not extract team codes from permissions, use fallback
  }
  
  // Fallback to common team codes
  return ['GD', 'GB', 'JP']
}

/**
 * Check team permission pattern by parsing the permission string
 * 
 * @param {string} permission - Permission string like 'read-GD-parcels'
 * @param {object} ability - CASL ability instance
 * @returns {boolean} - Whether the user has this permission
 */
function checkTeamPermissionPattern(permission, ability) {
  // Parse permission string like 'read-GD-parcels'
  if (permission.includes('-')) {
    const parts = permission.split('-')
    if (parts.length >= 3) {
      const action = parts[0]
      const team = parts[1].toUpperCase()
      const resource = parts.slice(2).join('-')
      
      // Try different subject formats
      const subjects = [
        `${team}-${resource}`,
        `${team}${resource.charAt(0).toUpperCase() + resource.slice(1)}`,
        resource
      ]
      
      return subjects.some(subject => {
        try {
          return ability.can(action, subject)
        } catch (error) {
          return false
        }
      })
    }
  }
  
  return false
}

/**
 * Check if user can view a navigation group based on children visibility
 * This extends the original canViewNavMenuGroup to use team-specific permission checking
 * 
 * @param {object} item - Navigation group item with children
 * @returns {boolean} - Whether the user can view the navigation group
 */
export function canViewNavMenuGroup(item) {
  // Check if any children are visible using team-specific permission checking
  const hasAnyVisibleChild = item.children?.some(child => {
    if (child.children) {
      // Recursive check for nested groups
      return canViewNavMenuGroup(child)
    }

    // Check individual navigation item
    return canViewNavItem(child.action, child.subject, { requiresSuperadmin: child.requiresSuperadmin, requiresAdmin: child.requiresAdmin })
  })

  // If the group itself has action and subject defined, check those too
  if (item.action && item.subject) {
    return canViewNavItem(item.action, item.subject, { requiresSuperadmin: item.requiresSuperadmin, requiresAdmin: item.requiresAdmin }) && hasAnyVisibleChild
  }

  // Otherwise, show group if any child is visible
  return hasAnyVisibleChild
}

/**
 * Enhanced navigation item visibility check that also considers permissions array
 * This is used for navigation items that might have multiple permission options
 * 
 * @param {object} item - Navigation item
 * @returns {boolean} - Whether the user can view the navigation item
 */
export function canViewNavMenuItem(item) {
  // If item has children, use group logic
  if (item.children) {
    return canViewNavMenuGroup(item)
  }
  
  // If item has a permissions array, check if user has any of those permissions
  if (item.permissions && Array.isArray(item.permissions)) {
    const { ability } = useAbility()
    
    return item.permissions.some(permission => {
      try {
        return ability.can(permission)
      } catch (error) {
        return checkTeamPermissionPattern(permission, ability)
      }
    })
  }
  
  // Default to basic action/subject check with team-specific fallback
  if (item.action && item.subject) {
    return canViewNavItem(item.action, item.subject, { requiresSuperadmin: item.requiresSuperadmin, requiresAdmin: item.requiresAdmin })
  }
  
  // If no permissions defined, show by default (for public items)
  return true
}
